'use client';

import { ItemTrackingStatus, type RoadMapUpdate } from '@/domain/roadmap/types';
import { Divider } from '@andritzot/metris-web-ui/data-display/divider';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { Checkbox } from '@andritzot/metris-web-ui/inputs/checkbox';
import { FormControlLabel } from '@andritzot/metris-web-ui/inputs/form-control';
import { Select } from '@andritzot/metris-web-ui/inputs/select';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Stack } from '@andritzot/metris-web-ui/layout/stack';
import { MenuItem } from '@andritzot/metris-web-ui/navigation/menu/menu-item';
import { Tab, Tabs } from '@andritzot/metris-web-ui/navigation/tabs';
import { Popover } from '@andritzot/metris-web-ui/utils/popover';
import FilterListOutlined from '@mui/icons-material/FilterListOutlined';
import {
  type Dispatch,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { BusinessRoadmapTabContent } from './BusinessRoadmapTabContent';
import { HistoryLogTabContent } from './HistoryLogTabContent';
import { type OverviewFormHandle, OverviewRoadmapTabContent } from './OverviewRoadmapTabContent';
import { PlanningBulkEditTrigger } from './PlanningBulkEdit';
import { PlanningView } from './PlanningView';

const STATUS_OPTIONS: ItemTrackingStatus[] = [
  ItemTrackingStatus.NotStarted,
  ItemTrackingStatus.InProgress,
  ItemTrackingStatus.Completed,
  ItemTrackingStatus.Delayed,
  ItemTrackingStatus.NotApplicable,
];

export enum RoadmapTab {
  Overview = 'tab-overview',
  Business = 'tab-business',
  Planning = 'tab-planning',
  HistoryLog = 'tab-history-log',
}

export type RoadmapTabKeys = `${RoadmapTab}`;

const TAB_LABELS: Record<RoadmapTab, string> = {
  [RoadmapTab.Overview]: 'Overview',
  [RoadmapTab.Business]: 'Business',
  [RoadmapTab.Planning]: 'Planning',
  [RoadmapTab.HistoryLog]: 'History Log',
};

const COMPACT_TABS_THRESHOLD = 600;

interface RoadmapTabsProps {
  roadmap: RoadMapUpdate | undefined;
  isLoading: boolean;
  activeTab: RoadmapTab;
  onTabChange: (tab: RoadmapTab) => void;
  overviewRef: RefObject<OverviewFormHandle | null>;
  hasSelectedItem: boolean;
  viewOnly: boolean;
  actions?: ReactNode;
  onIndeterminatePhaseIdsChange?: (ids: Set<number>) => void;
  onSave?: () => void;
  selectedPlanningItemIds: Set<number>;
  setSelectedPlanningItemIds: Dispatch<SetStateAction<Set<number>>>;
}

export function RoadmapTabs({
  roadmap,
  isLoading,
  activeTab,
  onTabChange,
  overviewRef,
  hasSelectedItem,
  viewOnly,
  actions,
  selectedPlanningItemIds,
  setSelectedPlanningItemIds,
}: Readonly<RoadmapTabsProps>) {
  const [statusFilter, setStatusFilter] = useState<ItemTrackingStatus[]>([]);
  const [qaFilter, setQaFilter] = useState<boolean[]>([]);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [isCompactTabs, setIsCompactTabs] = useState(false);
  const tabBarRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setIsCompactTabs(entry.contentRect.width < COMPACT_TABS_THRESHOLD);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const effectiveStatusFilter = useMemo(() => (hasSelectedItem ? statusFilter : []), [hasSelectedItem, statusFilter]);
  const effectiveQaFilter = useMemo(() => (hasSelectedItem ? qaFilter : []), [hasSelectedItem, qaFilter]);
  const activeFilterCount = useMemo(
    () => effectiveStatusFilter.length + effectiveQaFilter.length,
    [effectiveStatusFilter, effectiveQaFilter],
  );

  const handleFilterToggle = useCallback((status: ItemTrackingStatus) => {
    setStatusFilter((prev) => (prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]));
  }, []);

  const handleQaFilterToggle = useCallback((value: boolean) => {
    setQaFilter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  }, []);

  const handleOpenFilter = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchor(e.currentTarget);
  }, []);

  const handleCloseFilter = useCallback(() => setFilterAnchor(null), []);

  const handleClearFilters = useCallback(() => {
    setStatusFilter([]);
    setQaFilter([]);
  }, []);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          ref={tabBarRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            columnGap: 1,
            rowGap: 1,
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            pt: 2,
          }}
        >
          {isCompactTabs ? (
            <Select size="small" value={activeTab} onChange={(e) => onTabChange(e.target.value as RoadmapTab)}>
              {Object.values(RoadmapTab)
                .filter(Boolean)
                .map((tab) => (
                  <MenuItem key={tab} value={tab}>
                    {TAB_LABELS[tab]}
                  </MenuItem>
                ))}
            </Select>
          ) : (
            <Tabs
              type="secondary"
              scrollButtons={false}
              value={activeTab}
              onChange={(event: React.SyntheticEvent, newValue: RoadmapTab) => {
                event.preventDefault();
                onTabChange(newValue);
              }}
            >
              <Tab label="Overview" value={RoadmapTab.Overview} />
              <Tab label="Business" value={RoadmapTab.Business} />
              <Tab label="Planning" value={RoadmapTab.Planning} />
              <Tab label="History Log" value={RoadmapTab.HistoryLog} />
            </Tabs>
          )}
          {activeTab === RoadmapTab.Overview && <Box sx={{ alignSelf: 'flex-end', pb: 0.5 }}>{actions}</Box>}
          {activeTab === RoadmapTab.Planning && hasSelectedItem && (
            <Stack direction="row" spacing={2}>
              <PlanningBulkEditTrigger />
              <Button
                size="small"
                variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
                startIcon={<FilterListOutlined fontSize="small" />}
                onClick={handleOpenFilter}
              >
                Filter
                {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              </Button>
            </Stack>
          )}
        </Box>
        <Popover
          open={!!filterAnchor}
          anchorEl={filterAnchor}
          onClose={handleCloseFilter}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 1.5, minWidth: 180 }}>
            {STATUS_OPTIONS.map((status) => (
              <Box key={status} sx={{ display: 'block' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={effectiveStatusFilter.includes(status)}
                      onChange={() => handleFilterToggle(status)}
                    />
                  }
                  label={status}
                />
              </Box>
            ))}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'block' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={effectiveQaFilter.includes(true)}
                    onChange={() => handleQaFilterToggle(true)}
                  />
                }
                label="Approved"
              />
            </Box>
            <Box sx={{ display: 'block' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={effectiveQaFilter.includes(false)}
                    onChange={() => handleQaFilterToggle(false)}
                  />
                }
                label="Pending"
              />
            </Box>
            <Divider sx={{ my: 1 }} />
            <Button
              size="small"
              variant="text"
              onClick={handleClearFilters}
              disabled={activeFilterCount === 0}
              fullWidth
            >
              Clear
            </Button>
          </Box>
        </Popover>
        <Box sx={{ p: 2, overflow: 'auto', flex: 1, mr: -0.9 }}>
          <Box sx={{ display: activeTab === RoadmapTab.Overview ? 'block' : 'none', mr: 0 }}>
            <OverviewRoadmapTabContent ref={overviewRef} roadmap={roadmap} isLoading={isLoading} viewOnly={viewOnly} />
          </Box>
          <Box sx={{ display: activeTab === RoadmapTab.Business ? 'block' : 'none' }}>
            <BusinessRoadmapTabContent isLoading={isLoading} hasSelectedItem={hasSelectedItem} viewOnly={viewOnly} />
          </Box>
          {activeTab === RoadmapTab.Planning && (
            <PlanningView
              isLoading={isLoading}
              hasSelectedItem={hasSelectedItem}
              statusFilter={effectiveStatusFilter}
              qaFilter={effectiveQaFilter}
              selectedItemIds={selectedPlanningItemIds}
              onSelectionChange={setSelectedPlanningItemIds}
              viewOnly={viewOnly}
            />
          )}
          {activeTab === RoadmapTab.HistoryLog && (
            <HistoryLogTabContent roadmapId={roadmap?.idRoadmap} isLoading={isLoading} />
          )}
        </Box>
      </Box>
    </Box>
  );
}
